import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FirmService } from '../firm/firm.service';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { PrimeNG } from 'primeng/config';

export interface LocalizationSettings {
  language: string;
  calendar: any;
  culture: string;
  currency: string;
  datePipeFormat: string;
  dateTimePipeFormat: string;
  numberPipeFormat: LocalizedNumberFormat;
  integerPipeFormat: LocalizedNumberFormat;
  userTimezoneOffset: number;
  currencyPipeFormat: LocalizedNumberFormat;
}

export interface LocalizedNumberFormat {
  style: string;
  culture: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocalizationService {
  constructor(
    private firmService: FirmService,
    private primeNGconfig: PrimeNG,
    private translateService: TranslateService
  ) {
  }
  private currentLanguage = new BehaviorSubject('en');

  currentLanguage$: Observable<string> = this.currentLanguage.asObservable();

  get settings$(): Observable<LocalizationSettings> {
    return this.currentLanguage$.pipe(distinctUntilChanged(), map(lang => {
      const language = lang.toLocaleLowerCase();
      return {
        language,
        calendar: this.getCalendarLocalization(language),
        culture: this.getCulture(language),
        currency: this.getCurrency(),
        datePipeFormat: this.getDatePipeFormat(language),
        dateTimePipeFormat: this.getDateTimePipeFormat(language),
        numberPipeFormat: this.getNumberPipeFormat(language),
        integerPipeFormat: this.getIntegerPipeFormat(language),
        currencyPipeFormat: this.getCurrencyPipeFormat(language),
        userTimezoneOffset: (new Date().getTimezoneOffset()) * 60000
      } as LocalizationSettings;
    }));
  }

  changeLanguage(code: string): Observable<any> {
    this.primeNGconfig.setTranslation(this.getCalendarLocalization(code.toLocaleLowerCase()));

    localStorage.setItem('language', code);

    this.currentLanguage.next(code);

    return this.translateService.use(code);
  }

  getFirmLanguage(): string {
    return this.firmService.language.toLocaleLowerCase();
  }

  getFirmCurrencyCode(): string {
    return this.firmService.currency;
  }

  getCalendarLocalization(language: string): any {
    const no = {
      firstDayOfWeek: 1,
      dayNames: ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'],
      dayNamesShort: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'],
      dayNamesMin: ['Sø', 'Ma', 'Ti', 'On', 'To', 'Fr', 'Lø'],
      monthNames: ['Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'],
      monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'],
      today: 'I dag',
      clear: 'Tøm',
      dateFormat: 'dd.mm.yy',
      dateFormatPlaceholder: 'dd.mm.åååå',
      weekHeader: 'Uke',
      dateFrom: 'Dato fra',
      dateTo: 'Dato til'
    };

    const da = {
      firstDayOfWeek: 1,
      dayNames: ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'],
      dayNamesShort: ['Son', 'Mon', 'Die', 'Mit', 'Don', 'Fre', 'Sam'],
      dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      monthNames: ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August',
        'September', 'Oktober', 'November', 'December'],
      monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'],
      today: 'I dag',
      clear: 'Tom',
      dateFormat: 'dd.mm.yy',
      dateFormatPlaceholder: 'dd.mm.åååå',
      weekHeader: 'Uge',
      dateFrom: 'Dato fra',
      dateTo: 'Dato til'
    };

    const fi = {
      firstDayOfWeek: 1,
      dayNames: ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'],
      dayNamesShort: ['Sun', 'Maa', 'Tii', 'Kes', 'Tor', 'Per', 'Lau'],
      dayNamesMin: ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'],
      monthNames: ['Tammikuu', 'Helmikuu', 'Maaliskuu', 'Huhtikuu', 'Toukokuu', 'Kesäkuu', 'Heinäkuu', 'Elokuu', 'Syyskuu', 'Lokakuu', 'Marraskuu', 'Joulukuu'],
      monthNamesShort: ['Tam', 'Hel', 'Maa', 'Huh', 'Tou', 'Kes', 'Hei', 'Elo', 'Syy', 'Lok', 'Mar', 'Jou'],
      today: 'Tänään',
      clear: 'Tyhjennä',
      dateFormat: 'dd.mm.yy',
      dateFormatPlaceholder: 'pp.kk.vvvv',
      weekHeader: 'Viikko',
      dateFrom: 'Alkaen',
      dateTo: 'Asti'
    };

    const en = {
      firstDayOfWeek: 0,
      dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
        'September', 'October', 'November', 'December'],
      monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      today: 'Today',
      clear: 'Clear',
      dateFormat: 'dd.mm.yy',
      dateFormatPlaceholder: 'dd.mm.yyyy',
      weekHeader: 'Week',
      dateFrom: 'Date from',
      dateTo: 'Date to'
    };

    switch (language) {
      case 'no': return no;
      case 'da': return da;
      case 'fi': return fi;
      default: return en;
    }
  }

  private getCulture(language: string): string {
    switch (language) {
      case 'no': return 'nb-NO';
      case 'dk': return 'da-DK';
      default: return 'en-US';
    }
  }

  private getDatePipeFormat(language: string): string {
    switch (language) {
      case 'no': return 'dd.MM.yyyy';
      case 'da': return 'dd.MM.yyyy';
      default: return 'dd.MM.yyyy';
    }
  }

  private getDateTimePipeFormat(language: string): string {
    switch (language) {
      case 'no': return 'dd.MM.yyyy HH:mm:ss';
      case 'da': return 'dd.MM.yyyy HH:mm:ss';
      default: return 'dd.MM.yyyy HH:mm:ss';
    }
  }

  private getNumberPipeFormat(language: string) {
    return {
      style: '1.2-2',
      culture: this.getCulture(language)
    } as LocalizedNumberFormat;
  }

  private getIntegerPipeFormat(language: string) {
    return {
      style: '1.0-0',
      culture: this.getCulture(language)
    } as LocalizedNumberFormat;
  }

  private getCurrencyPipeFormat(language: string) {
    return {
      style: '1.0-0',
      culture: this.getCulture(language)
    } as LocalizedNumberFormat;
  }

  private getCurrency() {
    const firmCurrency = this.getFirmCurrencyCode().toLocaleLowerCase();
    switch (firmCurrency) {
      case 'nok': return 'kr';
      case 'dkk': return 'kr';
      default: return firmCurrency;
    }
  }
}
